import type { CommandEngine } from "./engineRegistry";

// ── default engine ──────────────────────────────────────────────────────────
// Routes through the action service (`/api/web/{slug}/{record}/{cmd}`). This
// is the floor of the resolution chain — canHandle ALWAYS returns true so any
// button dispatch lands here when no specialty engine resolves it.
export const defaultEngine: CommandEngine = {
    key: "default",
    canHandle: () => true,
    async dispatch(payload, ctx) {
        return ctx.services.action.runButton(
            payload.actionSlug,
            payload.recordUuid,
            payload.commandName,
            payload.context ?? undefined,
        );
    },
};

// ── workflow engine ─────────────────────────────────────────────────────────
// Treats `commandName` as a workflow transition CODE and routes to
// `/api/workflow/transition`. We don't have a sync "is-known-transition"
// lookup on the client, so canHandle accepts any commandName and the backend
// is the source of truth for "is this a valid transition?". If the transition
// isn't recognised the engine will throw — caller's error→toast middleware
// surfaces it. (Fallback to default would dispatch the same string as a model
// command and likely 404, which is no better.)
//
// We DO need a modelKey to call /api/workflow/transition. Engines don't get
// one in the dispatch payload — the actionSlug is action-scoped, not model-
// scoped. The action service exposes the modelKey via the open ActionLoad,
// but the engine layer is service-only. Convention: the platform handler
// must inject `modelKey` into payload.context before invoking the engine.
export const workflowEngine: CommandEngine = {
    key: "workflow",
    canHandle(payload) {
        // Need both a transition code (commandName) and a modelKey (via ctx).
        // Without modelKey we can't call /api/workflow/transition — fall back
        // to default so the button still works against the action API.
        const ctx = (payload.context ?? {}) as Record<string, unknown>;
        return Boolean(payload.commandName) && typeof ctx.modelKey === "string";
    },
    async dispatch(payload, ctx) {
        const pctx = (payload.context ?? {}) as Record<string, unknown>;
        const modelKey = String(pctx.modelKey ?? "");
        return ctx.services.workflow.transition(modelKey, payload.recordUuid, payload.commandName);
    },
};

// ── approval engine ─────────────────────────────────────────────────────────
// `commandName` is the decision verb (APPROVE / REJECT / RETURN). `recordUuid`
// is the case_id by convention (approval buttons render on case rows / case
// forms). Optional `context.comment` is forwarded.
export const approvalEngine: CommandEngine = {
    key: "approval",
    canHandle(payload) {
        const decision = (payload.commandName || "").toUpperCase();
        return decision === "APPROVE" || decision === "REJECT" || decision === "RETURN";
    },
    async dispatch(payload, ctx) {
        const decision = payload.commandName.toUpperCase() as "APPROVE" | "REJECT" | "RETURN";
        const pctx = (payload.context ?? {}) as Record<string, unknown>;
        const comment = typeof pctx.comment === "string" ? pctx.comment : undefined;
        await ctx.services.approval.decide(payload.recordUuid, decision, comment);
        return null;
    },
};

// ── report engine ───────────────────────────────────────────────────────────
// `commandName` is the report `action_key`. `recordUuid` is included in the
// targets list. Engine returns the report run result (inline-bytes-base64 OR
// persisted-storage-uuid) so the dispatcher / view can deliver the blob.
export const reportEngine: CommandEngine = {
    key: "report",
    canHandle: () => true,
    async dispatch(payload, ctx) {
        const pctx = (payload.context ?? {}) as Record<string, unknown>;
        const targets = Array.isArray(pctx.target_record_uuids)
            ? (pctx.target_record_uuids as unknown[]).map((v) => String(v))
            : [payload.recordUuid];
        return ctx.services.report.run(payload.commandName, {
            target_record_uuids: targets,
            params: (pctx.params as Record<string, unknown> | undefined) ?? undefined,
            format: typeof pctx.format === "string" ? pctx.format : undefined,
            persist: typeof pctx.persist === "boolean" ? pctx.persist : undefined,
        });
    },
};

// ── action engine ───────────────────────────────────────────────────────────
// `commandType="action"` resolves the button's `commandName` as an
// `ir.action` `path` slug, loads the action's `target`, and returns an
// envelope `interpretServerAction` routes to OPEN_ACTION (target=current
// or inline) or OPEN_ACTION_DIALOG (target=dialog). Lets a DSL button
// trigger a client-action open without a hardcoded kernel branch.
//
// Used by `<button command-type="action" command="<action-path-slug>"/>` in
// any view. The first adopter (foundation.presentation Enh 08 / Equipment
// 3D Viewer port) wires this from the equipment-type form + list views
// to open the 3D viewer as a dialog.
export const actionEngine: CommandEngine = {
    key: "action",
    canHandle(payload) {
        return payload.commandType === "action" && Boolean(payload.commandName);
    },
    async dispatch(payload, ctx) {
        const slug = payload.commandName;
        const load = await ctx.services.action.loadAction(slug);
        const target = load.action.target ?? "current";
        const recordId = payload.recordUuid || undefined;
        // Return an `ir.action.client` envelope — interpretServerAction
        // reads `target` and emits OPEN_ACTION_DIALOG when "dialog",
        // OPEN_ACTION otherwise.
        return {
            action_type: "ir.action.client",
            action_slug: slug,
            target,
            ...(recordId ? { record_id: recordId } : {}),
        };
    },
};

// ── core engines, registered at boot ────────────────────────────────────────
// Order matters only for telemetry (the registry resolves by exact key — never
// scans). Default LAST so it is registered as the floor that the resolver
// reaches via the fallback branch, not via the named-key branch.
export function registerCoreEngines(registry: { register: (e: CommandEngine) => () => void }): void {
    registry.register(workflowEngine);
    registry.register(approvalEngine);
    registry.register(reportEngine);
    registry.register(actionEngine);
    registry.register(defaultEngine);
}

// Engine ordering note for `usePlatformCommands`:
//   bus.use(pendingTrackMiddleware) → pre-records the signature
//   bus.register("RUN_BUTTON", handler) → handler resolves engine + dispatches
//   handler post-processes via interpretServerAction() → may dispatch follow-up
